/**
 * The console's voice.
 *
 * Four moments in a run are worth hearing from across the room — a shift
 * sighted, a claim going out, a seat refusing us, a shift taken — and this
 * plays them. Nothing else in the app makes a sound.
 *
 * ── Why there is no React in this file ──────────────────────────────
 * The trigger sits in `useRealtime`'s ingest path, which runs for every frame
 * the stream delivers for every customer. If announcing an event went through
 * component state, a busy window would re-render the shell once per sighting
 * on top of the work the grid is already doing — and the whole point of the
 * realtime layer's batching is that it does not do that.
 *
 * So this module owns its own state as plain module-level values, exposes a
 * synchronous `playRunEvent`, and never notifies React of anything. The one
 * piece of state the UI does read — whether sound is on — it reads through
 * `subscribe`/`getEnabled` with `useSyncExternalStore`, which re-renders one
 * button and nothing else.
 *
 * ── Cost when it is switched off ────────────────────────────────────
 * `wantsEvents()` is a boolean read. The caller checks it before touching a
 * line, so a console with sound off pays one comparison per SSE frame and
 * allocates nothing. No `AudioContext` is constructed, no impulse response is
 * generated, and no listener stays attached that would not be there anyway.
 *
 * ── Everything is synthesised ───────────────────────────────────────
 * Oscillators and one noise buffer, built at play time. No audio files, so
 * nothing to bundle, fetch, cache-bust or 404. The reverb is a procedurally
 * generated impulse response on ONE shared send bus — a convolver per voice
 * would mean ten rooms for ten simultaneous wins, which is the one thing here
 * expensive enough to matter.
 *
 * ── Except an operator's own uploads ────────────────────────────────
 * An event can also play a file the operator chose. The file is decoded ONCE —
 * at upload, and again at page load from IndexedDB — into an `AudioBuffer`
 * held in memory, so the announce path stays exactly as cheap as the
 * synthesised one: a `BufferSource` is a handle onto samples that already
 * exist, and starting one allocates nothing and waits for nothing. Decoding at
 * play time would mean an async gap on the one path that must not have one.
 */

import { RUN_EVENTS, type HuntSoundMetaDto, type RunEvent } from '@acs/shared'
import { API_BASE } from '@/lib/api'

/* ------------------------------------------------------------------ *
 * Preferences
 * ------------------------------------------------------------------ */

const ON_KEY = 'acs.sfx.on'
const VOL_KEY = 'acs.sfx.vol'
const VOICE_KEY_PREFIX = 'acs.sfx.voice.'

/**
 * Sound ships ON.
 *
 * That leaves one gap worth naming, because it cannot be closed from here:
 * browsers refuse to start audio until the page has seen a real gesture, so
 * between load and the operator's first click or keypress nothing can play
 * however this flag is set. The console does not pretend otherwise — the rail
 * button reports `ready()` separately from `enabled`, and says so until the
 * gesture lands. The first interaction of any kind, anywhere, opens the audio
 * device and warms the reverb, so in practice the window is the few seconds
 * before the operator touches anything.
 */
const DEFAULT_ENABLED = true
const DEFAULT_VOLUME = 0.55

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    // Private mode, or storage disabled by policy. Preferences stop persisting;
    // nothing else changes.
    return null
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* ignore */
  }
}

/* ------------------------------------------------------------------ *
 * Synthesis primitives
 * ------------------------------------------------------------------ */

type Ctx = BaseAudioContext

/**
 * Percussive envelope.
 *
 * `exponentialRampToValueAtTime` cannot reach or start from zero, hence the
 * floors — a literal 0 throws and would take out the whole voice. An attack
 * under a millisecond is what makes a transient read as a hit rather than as a
 * swell, and is the entire difference between a gunshot and a launch.
 */
function hit(param: AudioParam, t0: number, peak: number, attack: number, release: number): void {
  const top = Math.max(0.0002, peak)
  param.setValueAtTime(0.0001, t0)
  param.exponentialRampToValueAtTime(top, t0 + attack)
  param.exponentialRampToValueAtTime(0.0001, t0 + release)
}

/**
 * One noise buffer per context, two seconds long.
 *
 * Long enough that every layer can start at a random offset and still find
 * enough samples for its tail — which is what stops forty misses in a row from
 * sounding like one stuck sample.
 */
const noiseCache = new WeakMap<Ctx, AudioBuffer>()
function noise(c: Ctx): AudioBuffer {
  const cached = noiseCache.get(c)
  if (cached) return cached
  const len = Math.ceil(c.sampleRate * 2)
  const buf = c.createBuffer(1, len, c.sampleRate)
  const d = buf.getChannelData(0)
  for (let i = 0; i < len; i += 1) d[i] = Math.random() * 2 - 1
  noiseCache.set(c, buf)
  return buf
}

/** Decaying stereo noise — a synthetic room. Built once per context. */
function reverbIR(c: Ctx, seconds: number, decay: number): AudioBuffer {
  const len = Math.ceil(c.sampleRate * seconds)
  const buf = c.createBuffer(2, len, c.sampleRate)
  for (let ch = 0; ch < 2; ch += 1) {
    const d = buf.getChannelData(ch)
    for (let i = 0; i < len; i += 1) {
      d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay)
    }
  }
  return buf
}

/**
 * The single reverb send, built with the context and shared by every voice.
 *
 * Module-level rather than derived from whatever node a layer happens to be
 * writing to, because voices do NOT write to the master any more — each one
 * gets its own panner (see `fire`). Routing the send through that panner would
 * put the room inside one voice's position, which is both wrong and a slow
 * leak of convolvers. Dry is placed; wet is shared and wide, exactly as a
 * mixing desk does it.
 */
let wetSend: GainNode | null = null

/**
 * Per-voice shading, set immediately before `build` and read by the layers.
 *
 * A module-level value rather than a parameter threaded through nine helper
 * signatures. Safe because `build` is wholly synchronous: nothing can run
 * between the assignment and the last layer it creates, so there is no
 * interleaving to race. Reset to neutral after every fire.
 */
let shadeCents = 0
let shadeRate = 1

interface Routed {
  wet?: number
}

/** Route a finished layer to its voice's dry output, and optionally to the room. */
function out(c: Ctx, node: AudioNode, dest: AudioNode, o: Routed): void {
  node.connect(dest)
  if (o.wet && wetSend) {
    const s = c.createGain()
    s.gain.value = o.wet
    node.connect(s)
    s.connect(wetSend)
  }
}

/** Soft clip. A little drive is what stops a synth tone sounding like a test oscillator. */
function shaper(c: Ctx, amount: number): WaveShaperNode {
  const n = 1024
  const curve = new Float32Array(n)
  for (let i = 0; i < n; i += 1) {
    const x = (i * 2) / n - 1
    curve[i] = ((1 + amount) * x) / (1 + amount * Math.abs(x))
  }
  const ws = c.createWaveShaper()
  ws.curve = curve
  ws.oversample = '2x'
  return ws
}

interface NoiseOpts extends Routed {
  type: BiquadFilterType
  freq: number
  sweepTo?: number
  q?: number
  peak: number
  attack?: number
  release: number
}

function noiseLayer(c: Ctx, dest: AudioNode, t0: number, o: NoiseOpts): void {
  const src = c.createBufferSource()
  src.buffer = noise(c)
  // Noise has no pitch to detune, so it is shaded by resampling instead —
  // which moves its spectral centre and separates two shots the same way.
  src.playbackRate.value = shadeRate
  const f = c.createBiquadFilter()
  f.type = o.type
  f.frequency.value = o.freq
  if (o.q !== undefined) f.Q.value = o.q
  if (o.sweepTo) {
    f.frequency.setValueAtTime(o.freq, t0)
    f.frequency.exponentialRampToValueAtTime(o.sweepTo, t0 + o.release)
  }
  const g = c.createGain()
  hit(g.gain, t0, o.peak, o.attack ?? 0.0006, o.release)
  src.connect(f).connect(g)
  out(c, g, dest, o)
  // Offset capped so even the longest tail here still finds buffer to read.
  src.start(t0, Math.random() * 1.2)
  src.stop(t0 + o.release + 0.05)
}

interface ToneOpts extends Routed {
  wave?: OscillatorType
  freq: number
  to?: number
  glide?: number
  lowpass?: number
  peak: number
  attack?: number
  release: number
}

function toneLayer(c: Ctx, dest: AudioNode, t0: number, o: ToneOpts): void {
  const osc = c.createOscillator()
  osc.type = o.wave ?? 'sine'
  osc.detune.value = shadeCents
  osc.frequency.setValueAtTime(o.freq, t0)
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t0 + (o.glide ?? o.release))
  let node: AudioNode = osc
  if (o.lowpass) {
    const lp = c.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = o.lowpass
    osc.connect(lp)
    node = lp
  }
  const g = c.createGain()
  hit(g.gain, t0, o.peak, o.attack ?? 0.002, o.release)
  node.connect(g)
  out(c, g, dest, o)
  osc.start(t0)
  osc.stop(t0 + o.release + 0.05)
}

interface SweepOpts extends Routed {
  wave?: OscillatorType
  freq: number
  to: number
  glide?: number
  filt: number
  filtTo: number
  q?: number
  drive?: number
  peak: number
  attack?: number
  release: number
}

/**
 * Pitch and filter cutoff sweeping together through a resonant lowpass.
 *
 * The resonance tracking the pitch down is the signature of synthesised weapon
 * fire — it is what reads as an energy discharge rather than a retro pew.
 */
function sweepLayer(c: Ctx, dest: AudioNode, t0: number, o: SweepOpts): void {
  const end = t0 + (o.glide ?? o.release)
  const osc = c.createOscillator()
  osc.type = o.wave ?? 'sawtooth'
  osc.detune.value = shadeCents
  osc.frequency.setValueAtTime(o.freq, t0)
  osc.frequency.exponentialRampToValueAtTime(o.to, end)

  const f = c.createBiquadFilter()
  f.type = 'lowpass'
  f.Q.value = o.q ?? 8
  f.frequency.setValueAtTime(o.filt, t0)
  f.frequency.exponentialRampToValueAtTime(o.filtTo, end)

  const g = c.createGain()
  hit(g.gain, t0, o.peak, o.attack ?? 0.0008, o.release)

  let head: AudioNode = osc
  if (o.drive) {
    const ws = shaper(c, o.drive)
    osc.connect(ws)
    head = ws
  }
  head.connect(f).connect(g)
  out(c, g, dest, o)
  osc.start(t0)
  osc.stop(t0 + o.release + 0.05)
}

interface ChordOpts extends Routed {
  freq: number
  wave?: OscillatorType
  detune?: number[]
  lowpass?: number
  lpTo?: number
  lpTime?: number
  q?: number
  peak: number
  attack?: number
  release: number
}

/**
 * One chord note as a detuned unison through an opening filter.
 *
 * Two saws a few cents apart beat against each other into a slow chorus. It is
 * the cheapest thing that separates a chord sounding like a synth demo from one
 * sounding like a record.
 */
function chordNote(c: Ctx, dest: AudioNode, t0: number, o: ChordOpts): void {
  const dets = o.detune ?? [-6, 6]
  const f = c.createBiquadFilter()
  f.type = 'lowpass'
  f.Q.value = o.q ?? 0.9
  f.frequency.setValueAtTime(o.lowpass ?? 2600, t0)
  if (o.lpTo) f.frequency.exponentialRampToValueAtTime(o.lpTo, t0 + (o.lpTime ?? o.release))

  const g = c.createGain()
  hit(g.gain, t0, o.peak, o.attack ?? 0.008, o.release)
  f.connect(g)
  out(c, g, dest, o)

  for (const cents of dets) {
    const osc = c.createOscillator()
    osc.type = o.wave ?? 'sawtooth'
    osc.frequency.value = o.freq
    // The unison spread and the per-customer shade are both detune, so they
    // simply add — the chorus is preserved and the whole note moves with it.
    osc.detune.value = cents + shadeCents
    osc.connect(f)
    osc.start(t0)
    osc.stop(t0 + o.release + 0.06)
  }
}

interface BellOpts extends Routed {
  freq: number
  peak: number
  attack?: number
  release: number
}

/**
 * A struck bell: partials at non-integer ratios, the higher ones dying first.
 *
 * Real bells are inharmonic — that is why a bell is recognisably a bell. The
 * staggered decay is what makes the strike bright and the tail pure.
 */
function bell(c: Ctx, dest: AudioNode, t0: number, o: BellOpts): void {
  const ratios = [1, 2.0, 2.76, 5.4, 8.93]
  const amps = [1, 0.32, 0.2, 0.085, 0.035]
  for (let i = 0; i < ratios.length; i += 1) {
    const osc = c.createOscillator()
    osc.type = 'sine'
    osc.detune.value = shadeCents
    osc.frequency.value = o.freq * (ratios[i] as number)
    const g = c.createGain()
    hit(
      g.gain,
      t0,
      o.peak * (amps[i] as number),
      o.attack ?? 0.003,
      Math.max(0.08, o.release * (1 - i * 0.14))
    )
    osc.connect(g)
    out(c, g, dest, o)
    osc.start(t0)
    osc.stop(t0 + o.release + 0.06)
  }
}

/* ------------------------------------------------------------------ *
 * The voices
 *
 * To change what an event sounds like, change `DEFAULT_VOICE` below — or call
 * `setVoice(event, id)` at runtime, which persists and takes effect on the
 * next event with no reload. Adding a voice means adding one entry here; the
 * registry is the only place that needs to know about it.
 * ------------------------------------------------------------------ */

interface Voice {
  id: string
  label: string
  /** Seconds until the last node is silent. Drives the polyphony budget only. */
  dur: number
  build: (c: Ctx, dest: AudioNode, t0: number) => void
}

export const VOICES: Readonly<Record<RunEvent, readonly Voice[]>> = Object.freeze({
  active: [
    {
      id: 'sonar',
      label: 'Sonar ping',
      dur: 1.1,
      build(c, d, t) {
        // A ping and its echo — unmistakably "contact", and nothing like the
        // claim's crack a minute later. The wet send does the distance.
        bell(c, d, t, { freq: 1174.66, peak: 0.28, release: 0.55, wet: 0.75 })
        bell(c, d, t + 0.22, { freq: 1174.66, peak: 0.1, release: 0.45, wet: 0.85 })
      },
    },
    {
      id: 'scramble',
      label: 'Scramble',
      dur: 0.55,
      build(c, d, t) {
        // Three quick steps UP — the mirror of the miss's two-tone down, so
        // the pair read as question and answer across the wait between them.
        toneLayer(c, d, t, { wave: 'square', freq: 659.25, lowpass: 2400, peak: 0.16, attack: 0.004, release: 0.09 })
        toneLayer(c, d, t + 0.11, { wave: 'square', freq: 880, lowpass: 2600, peak: 0.18, attack: 0.004, release: 0.16 })
        toneLayer(c, d, t + 0.22, { wave: 'square', freq: 1108.73, lowpass: 2800, peak: 0.16, attack: 0.004, release: 0.22, wet: 0.3 })
      },
    },
    {
      id: 'radar',
      label: 'Radar sweep',
      dur: 0.7,
      build(c, d, t) {
        sweepLayer(c, d, t, {
          wave: 'triangle', freq: 420, to: 1680, glide: 0.16,
          filt: 1200, filtTo: 5200, q: 5,
          peak: 0.15, attack: 0.01, release: 0.2, wet: 0.35,
        })
        toneLayer(c, d, t + 0.17, { freq: 1568, peak: 0.14, attack: 0.003, release: 0.4, wet: 0.5 })
      },
    },
  ],

  burst: [
    {
      id: 'rail',
      label: 'Rail',
      dur: 0.48,
      build(c, d, t) {
        // A capacitor winding up, then the crack, then two inharmonic partials
        // ringing off the rails.
        sweepLayer(c, d, t, {
          wave: 'triangle', freq: 320, to: 1650, glide: 0.034,
          filt: 2600, filtTo: 6200, q: 3,
          peak: 0.13, attack: 0.008, release: 0.038,
        })
        const f = t + 0.035
        noiseLayer(c, d, f, { type: 'highpass', freq: 4000, peak: 0.6, attack: 0.0003, release: 0.026 })
        toneLayer(c, d, f, { freq: 1240, peak: 0.16, attack: 0.002, release: 0.3 })
        toneLayer(c, d, f, { freq: 1867, peak: 0.085, attack: 0.002, release: 0.22 })
        toneLayer(c, d, f, { freq: 138, to: 38, glide: 0.06, peak: 0.42, attack: 0.0012, release: 0.1 })
        noiseLayer(c, d, f + 0.01, { type: 'bandpass', freq: 1800, q: 0.8, peak: 0.06, attack: 0.005, release: 0.22 })
      },
    },
    {
      id: 'pulse',
      label: 'Pulse',
      dur: 0.3,
      build(c, d, t) {
        noiseLayer(c, d, t, { type: 'highpass', freq: 3200, peak: 0.34, attack: 0.0004, release: 0.014 })
        sweepLayer(c, d, t, {
          wave: 'sawtooth', freq: 780, to: 128, glide: 0.055,
          filt: 5200, filtTo: 620, q: 7, drive: 6,
          peak: 0.44, attack: 0.0008, release: 0.095,
        })
        toneLayer(c, d, t, { freq: 160, to: 46, glide: 0.04, peak: 0.3, attack: 0.001, release: 0.07 })
        noiseLayer(c, d, t + 0.01, { type: 'bandpass', freq: 2600, q: 1.4, peak: 0.05, attack: 0.004, release: 0.14 })
      },
    },
    {
      id: 'patt',
      label: 'Patt (acoustic)',
      dur: 0.28,
      build(c, d, t) {
        noiseLayer(c, d, t, { type: 'highpass', freq: 2600, peak: 0.62, attack: 0.0004, release: 0.035 })
        noiseLayer(c, d, t + 0.001, { type: 'bandpass', freq: 950, q: 0.7, peak: 0.5, attack: 0.0008, release: 0.075 })
        toneLayer(c, d, t, { freq: 190, to: 58, glide: 0.045, peak: 0.42, attack: 0.001, release: 0.065 })
        noiseLayer(c, d, t + 0.012, { type: 'lowpass', freq: 1500, sweepTo: 420, peak: 0.09, attack: 0.006, release: 0.16 })
      },
    },
  ],

  miss: [
    {
      id: 'twotone',
      label: 'Two-tone',
      dur: 0.4,
      build(c, d, t) {
        toneLayer(c, d, t, { wave: 'sine', freq: 523.25, lowpass: 1200, peak: 0.3, attack: 0.005, release: 0.15 })
        toneLayer(c, d, t + 0.08, { wave: 'sine', freq: 415.3, lowpass: 1000, peak: 0.3, attack: 0.005, release: 0.22 })
      },
    },
    {
      id: 'dryfire',
      label: 'Dry fire',
      dur: 0.3,
      build(c, d, t) {
        noiseLayer(c, d, t, { type: 'bandpass', freq: 2400, q: 4.5, peak: 0.4, attack: 0.0005, release: 0.028 })
        toneLayer(c, d, t + 0.004, { wave: 'triangle', freq: 215, to: 128, glide: 0.06, lowpass: 720, peak: 0.34, attack: 0.002, release: 0.14 })
      },
    },
    {
      id: 'dud',
      label: 'Dud',
      dur: 0.34,
      build(c, d, t) {
        noiseLayer(c, d, t, { type: 'lowpass', freq: 900, sweepTo: 260, peak: 0.2, attack: 0.004, release: 0.16 })
        toneLayer(c, d, t, { wave: 'sine', freq: 392, to: 233, glide: 0.13, lowpass: 1100, peak: 0.3, attack: 0.006, release: 0.2 })
      },
    },
  ],

  won: [
    {
      id: 'ascension',
      label: 'Ascension',
      dur: 2.6,
      build(c, d, t) {
        // A quiet riser leading in, so the chord arrives rather than starts.
        noiseLayer(c, d, t, {
          type: 'bandpass', freq: 500, sweepTo: 5000, q: 1.2,
          peak: 0.07, attack: 0.16, release: 0.3, wet: 0.4,
        })
        // Root an octave below the chord — weight without mud.
        toneLayer(c, d, t + 0.02, { freq: 65.41, peak: 0.34, attack: 0.025, release: 1.3, wet: 0.15 })

        const seq = [523.25, 659.25, 783.99, 987.77, 1174.66]
        for (let i = 0; i < seq.length; i += 1) {
          const last = i === seq.length - 1
          chordNote(c, d, t + i * 0.058, {
            freq: seq[i] as number,
            wave: 'sawtooth', detune: [-7, 7],
            lowpass: 1100, lpTo: 4600, lpTime: 0.35,
            peak: 0.1, attack: 0.006, release: last ? 1.5 : 0.85,
            wet: 0.55,
          })
        }
        bell(c, d, t + 0.26, { freq: 1567.98, peak: 0.17, attack: 0.003, release: 1.25, wet: 0.7 })
      },
    },
    {
      id: 'lock',
      label: 'Signal lock',
      dur: 2.2,
      build(c, d, t) {
        sweepLayer(c, d, t, {
          wave: 'triangle', freq: 210, to: 1350, glide: 0.26,
          filt: 1600, filtTo: 7000, q: 4,
          peak: 0.13, attack: 0.02, release: 0.28, wet: 0.3,
        })
        const k = t + 0.275
        noiseLayer(c, d, k, { type: 'highpass', freq: 5000, peak: 0.36, attack: 0.0004, release: 0.022 })
        toneLayer(c, d, k, { freq: 130.81, to: 65.41, glide: 0.05, peak: 0.4, attack: 0.0015, release: 0.7, wet: 0.15 })
        const triad = [523.25, 783.99, 1046.5]
        for (let i = 0; i < triad.length; i += 1) {
          chordNote(c, d, k + i * 0.012, {
            freq: triad[i] as number,
            wave: 'sawtooth', detune: [-8, 8],
            lowpass: 900, lpTo: 5200, lpTime: 0.3,
            peak: 0.12, attack: 0.004, release: 1.1, wet: 0.6,
          })
        }
        bell(c, d, k + 0.07, { freq: 2093, peak: 0.09, release: 0.9, wet: 0.75 })
      },
    },
    {
      id: 'bloom',
      label: 'Bloom',
      dur: 2.8,
      build(c, d, t) {
        const voicing = [130.81, 196.0, 261.63, 329.63, 392.0, 587.33]
        for (let i = 0; i < voicing.length; i += 1) {
          chordNote(c, d, t + i * 0.026, {
            freq: voicing[i] as number,
            wave: 'sawtooth', detune: [-5, 5],
            lowpass: 380, lpTo: 3900, lpTime: 0.75, q: 1.4,
            peak: i === 0 ? 0.13 : 0.085,
            attack: 0.07, release: 1.7, wet: 0.65,
          })
        }
        noiseLayer(c, d, t, {
          type: 'bandpass', freq: 1400, sweepTo: 3600, q: 0.9,
          peak: 0.045, attack: 0.25, release: 1.1, wet: 0.8,
        })
        bell(c, d, t + 0.5, { freq: 1046.5, peak: 0.075, attack: 0.02, release: 1.4, wet: 0.85 })
      },
    },
    {
      id: 'chime',
      label: 'Chime',
      dur: 1.6,
      build(c, d, t) {
        bell(c, d, t, { freq: 659.25, peak: 0.3, release: 0.75, wet: 0.7 })
        bell(c, d, t + 0.13, { freq: 987.77, peak: 0.28, release: 1.1, wet: 0.8 })
      },
    },
  ],
})

/** The chosen sound for each event. Change these to change the console's voice. */
const DEFAULT_VOICE: Readonly<Record<RunEvent, string>> = Object.freeze({
  active: 'sonar',
  burst: 'rail',
  miss: 'twotone',
  won: 'ascension',
})

/* ------------------------------------------------------------------ *
 * Uploaded sounds
 *
 * One optional file per event, chosen by the operator on the settings page.
 * The raw bytes live in IndexedDB — they are megabytes, and localStorage is
 * both string-typed and quota-shared with the preferences above — and the
 * DECODED buffer lives here in memory, which is the entire trick: the announce
 * path never decodes, never fetches, never awaits. It starts a `BufferSource`
 * over samples that are already sitting in RAM, which costs the same as any
 * synthesised voice.
 * ------------------------------------------------------------------ */

/** The voice id an event carries when it plays the operator's file. */
export const CUSTOM_VOICE_ID = 'custom'

export const MAX_CUSTOM_BYTES = 3 * 1024 * 1024
/**
 * Ten seconds, and this one is not negotiable upward casually: a win heard
 * from three customers at once holds three polyphony slots for its whole
 * length, and `MAX_SOUNDING` is budgeted around voices under three seconds.
 */
export const MAX_CUSTOM_SECONDS = 10

interface CustomSound {
  /** The uploaded file's name, shown in the picker. */
  name: string
  /**
   * Decoded and play-ready. Null only during the brief load-time decode.
   *
   * Deliberately NOT accompanied by the original encoded bytes: IndexedDB
   * holds the durable copy and nothing re-reads the encoding after decode, so
   * keeping it here would pin up to `MAX_CUSTOM_BYTES` per event of dead
   * memory for the life of the tab.
   */
  buffer: AudioBuffer | null
}

const customSounds = new Map<RunEvent, CustomSound>()

const IDB_NAME = 'acs-sfx'
/** The operator's own uploads, private to this browser. */
const IDB_STORE = 'sounds'
/** This browser's cached copy of the fleet-wide uploads. See `fleetSounds`. */
const IDB_FLEET_STORE = 'fleet'

/**
 * Version 2 added the `fleet` store. `onupgradeneeded` creates whichever
 * stores are missing rather than assuming a version, so a v1 database (local
 * uploads only) upgrades in place without touching its rows.
 */
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 2)
    req.onupgradeneeded = () => {
      for (const store of [IDB_STORE, IDB_FLEET_STORE]) {
        if (!req.result.objectStoreNames.contains(store)) req.result.createObjectStore(store)
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('IndexedDB refused to open'))
  })
}

function idbReq<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error ?? new Error('IndexedDB request failed'))
  })
}

async function idbLoadAll(storeName: string): Promise<Map<RunEvent, Record<string, unknown>>> {
  const db = await openDb()
  try {
    const store = db.transaction(storeName, 'readonly').objectStore(storeName)
    // All gets issued before any await: an idle IndexedDB transaction commits
    // itself at the end of the microtask, so a get issued after one resolves
    // would land on a transaction that no longer exists.
    const rows = await Promise.all(RUN_EVENTS.map((e) => idbReq<unknown>(store.get(e))))
    const out = new Map<RunEvent, Record<string, unknown>>()
    for (let i = 0; i < RUN_EVENTS.length; i += 1) {
      const row = rows[i]
      if (row && typeof row === 'object' && !Array.isArray(row)) {
        out.set(RUN_EVENTS[i] as RunEvent, row as Record<string, unknown>)
      }
    }
    return out
  } finally {
    db.close()
  }
}

async function idbPut(storeName: string, event: RunEvent, rec: object): Promise<void> {
  const db = await openDb()
  try {
    const tx = db.transaction(storeName, 'readwrite')
    tx.objectStore(storeName).put(rec, event)
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB write failed'))
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB write aborted'))
    })
  } finally {
    db.close()
  }
}

async function idbDelete(storeName: string, event: RunEvent): Promise<void> {
  const db = await openDb()
  try {
    const tx = db.transaction(storeName, 'readwrite')
    tx.objectStore(storeName).delete(event)
    await new Promise<void>((resolve) => {
      tx.oncomplete = () => resolve()
      tx.onerror = () => resolve()
      tx.onabort = () => resolve()
    })
  } finally {
    db.close()
  }
}

/** A stored local-upload row, shape-checked out of the untyped store read. */
function asCustomRow(row: Record<string, unknown>): { name: string; bytes: ArrayBuffer } | null {
  if (typeof row['name'] === 'string' && row['bytes'] instanceof ArrayBuffer) {
    return { name: row['name'], bytes: row['bytes'] }
  }
  return null
}

/**
 * Decode encoded audio into a play-ready buffer.
 *
 * Uses the live context when one is open; otherwise a throwaway
 * `OfflineAudioContext`, which — unlike the real one — needs no user gesture,
 * so a page can decode its stored sounds the moment it loads. A buffer decoded
 * by one context plays fine in another; a sample-rate mismatch is resampled by
 * the `BufferSource` itself.
 *
 * The bytes are COPIED in (`slice`), because `decodeAudioData` detaches the
 * buffer it is handed, and the original must survive to be persisted.
 */
async function decodeBytes(bytes: ArrayBuffer): Promise<AudioBuffer> {
  const c: BaseAudioContext = ctx ?? new OfflineAudioContext(1, 1, 44100)
  return c.decodeAudioData(bytes.slice(0))
}

/** The operator's file as a voice, or null while it has no decoded buffer. */
function customVoice(event: RunEvent): Voice | null {
  const s = customSounds.get(event)
  if (!s || !s.buffer) return null
  const buf = s.buffer
  return {
    id: CUSTOM_VOICE_ID,
    label: s.name,
    dur: buf.duration,
    build(c, d, t) {
      const src = c.createBufferSource()
      src.buffer = buf
      // The per-customer shade — see `SPREAD`. A file has no detune param, so
      // it is shaded the way the noise layers are: by playback rate.
      src.playbackRate.value = shadeRate
      const g = c.createGain()
      // Uploaded files are often mastered to full scale; a little headroom
      // keeps two simultaneous copies inside the limiter's comfort zone.
      g.gain.value = 0.85
      src.connect(g)
      g.connect(d)
      src.start(t)
    },
  }
}

/* ------------------------------------------------------------------ *
 * Fleet sounds
 *
 * The uploads everyone shares. The FILES live on the server (Mongo, behind
 * `/api/automation/sounds`); what lives here is this console's decoded,
 * play-ready copy — fetched once, decoded once, and cached raw in IndexedDB
 * so the next page load decodes from disk instead of the network. Three
 * things keep it correct and current:
 *
 *   1. At load, the IndexedDB cache hydrates immediately (sound available
 *      even before the network answers), then the meta list is fetched and
 *      reconciled.
 *   2. A `sounds.changed` SSE frame carries the full meta list; open consoles
 *      reconcile against it within seconds of anyone uploading, no refresh.
 *   3. Reconciling is version-driven: only a file whose version moved is
 *      re-downloaded, and files removed on the server are removed here.
 *
 * None of which touches the announce path: `playRunEvent` still does a
 * synchronous `BufferSource` start over samples already in RAM.
 * ------------------------------------------------------------------ */

/** The voice id an event carries when it plays the fleet-wide upload. */
export const FLEET_VOICE_ID = 'fleet'

interface FleetSound {
  name: string
  version: number
  /** Decoded and play-ready. Null only while a fetch's decode is in flight. */
  buffer: AudioBuffer | null
}

const fleetSounds = new Map<RunEvent, FleetSound>()

/** The fleet upload as a voice, or null while it has no decoded buffer. */
function fleetVoice(event: RunEvent): Voice | null {
  const s = fleetSounds.get(event)
  if (!s || !s.buffer) return null
  const buf = s.buffer
  return {
    id: FLEET_VOICE_ID,
    label: s.name,
    dur: buf.duration,
    build(c, d, t) {
      const src = c.createBufferSource()
      src.buffer = buf
      src.playbackRate.value = shadeRate
      const g = c.createGain()
      g.gain.value = 0.85
      src.connect(g)
      g.connect(d)
      src.start(t)
    },
  }
}

/**
 * Reconcile this console against the server's meta list.
 *
 * Idempotent and last-writer-wins: metas arriving while a sync runs are
 * queued and applied after it, so two SSE frames in quick succession settle
 * on the newest. Every failure is per-event and non-fatal — a download that
 * breaks leaves the previous copy (or the synth fallback) playing.
 */
let fleetSyncRunning = false
let fleetSyncQueued: HuntSoundMetaDto[] | null = null

export async function syncFleetSounds(rawMetas: HuntSoundMetaDto[]): Promise<void> {
  /*
   * Shape-checked despite the type: the list arrives over SSE and HTTP as
   * unvalidated JSON, and the loop below DELETES every cached sound the list
   * does not name — so a malformed frame must be discarded per-entry rather
   * than allowed to masquerade as "the fleet has no sounds".
   */
  const metas = (Array.isArray(rawMetas) ? rawMetas : []).filter(
    (m): m is HuntSoundMetaDto =>
      !!m &&
      typeof m === 'object' &&
      (RUN_EVENTS as readonly string[]).includes((m as { event?: unknown }).event as string) &&
      typeof (m as { name?: unknown }).name === 'string' &&
      typeof (m as { version?: unknown }).version === 'number'
  )
  if (metas.length !== (Array.isArray(rawMetas) ? rawMetas.length : 0)) return

  if (fleetSyncRunning) {
    fleetSyncQueued = metas
    return
  }
  fleetSyncRunning = true
  try {
    let changed = false
    const present = new Set(metas.map((m) => m.event))

    for (const event of [...fleetSounds.keys()]) {
      if (!present.has(event)) {
        fleetSounds.delete(event)
        void idbDelete(IDB_FLEET_STORE, event)
        changed = true
      }
    }

    for (const meta of metas) {
      const existing = fleetSounds.get(meta.event)
      if (existing && existing.version === meta.version) continue
      try {
        const res = await fetch(
          `${API_BASE}/api/automation/sounds/${meta.event}/file?v=${meta.version}`,
          { credentials: 'include' }
        )
        if (!res.ok) continue
        const bytes = await res.arrayBuffer()
        const buffer = await decodeBytes(bytes)
        fleetSounds.set(meta.event, { name: meta.name, version: meta.version, buffer })
        void idbPut(IDB_FLEET_STORE, meta.event, {
          name: meta.name,
          version: meta.version,
          bytes,
        })
        changed = true
      } catch {
        // Network or decode failure: keep what we have; the next frame or
        // page load tries again.
      }
    }

    if (changed) emit()
  } finally {
    fleetSyncRunning = false
    const queued = fleetSyncQueued
    fleetSyncQueued = null
    if (queued) void syncFleetSounds(queued)
  }
}

/**
 * Ask the server for the current meta list and reconcile against it.
 *
 * Called once when the realtime layer comes up. Quiet on every failure — a
 * console that cannot reach the API right now still plays synth voices and
 * whatever the IndexedDB cache held, and the next SSE frame heals it.
 */
export async function refreshFleetSounds(): Promise<void> {
  try {
    const res = await fetch(`${API_BASE}/api/automation/sounds`, { credentials: 'include' })
    if (!res.ok) return
    const body: unknown = await res.json()
    const sounds =
      body && typeof body === 'object' ? (body as { sounds?: unknown }).sounds : null
    if (Array.isArray(sounds)) await syncFleetSounds(sounds as HuntSoundMetaDto[])
  } catch {
    /* offline, logged out, restarting — all fine, all self-healing */
  }
}

/** The fleet upload's name for an event, or null when none exists. */
export function getFleetSoundName(event: RunEvent): string | null {
  return fleetSounds.get(event)?.name ?? null
}

/** Whether the fleet upload is decoded and would actually play right now. */
export function isFleetSoundReady(event: RunEvent): boolean {
  return fleetSounds.get(event)?.buffer != null
}

/**
 * Upload a file as the fleet-wide sound for an event — every console gets it.
 *
 * The file is DECODED LOCALLY FIRST, before any byte leaves this machine:
 * that validates the format and the duration cap with the same engine that
 * will play it everywhere, so a file the server accepts is a file every
 * console can decode. Returns an error sentence for the picker, or null.
 */
export async function setFleetSound(event: RunEvent, file: File): Promise<string | null> {
  if (file.size > MAX_CUSTOM_BYTES) {
    return `That file is ${(file.size / (1024 * 1024)).toFixed(1)}MB — the limit is ${Math.round(MAX_CUSTOM_BYTES / (1024 * 1024))}MB.`
  }
  let bytes: ArrayBuffer
  try {
    bytes = await file.arrayBuffer()
  } catch {
    return 'Could not read the file.'
  }
  let buffer: AudioBuffer
  try {
    buffer = await decodeBytes(bytes)
  } catch {
    return 'This browser could not decode that file. MP3, WAV and OGG are safe choices.'
  }
  if (buffer.duration > MAX_CUSTOM_SECONDS) {
    return `That clip runs ${buffer.duration.toFixed(1)}s — the limit is ${MAX_CUSTOM_SECONDS}s, so a storm of events cannot stack minutes of audio.`
  }

  let meta: HuntSoundMetaDto
  try {
    const res = await fetch(`${API_BASE}/api/automation/sounds/${event}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'content-type': file.type !== '' ? file.type : 'application/octet-stream',
        'x-sound-name': encodeURIComponent(file.name),
      },
      body: bytes,
    })
    if (!res.ok) {
      const body: unknown = await res.json().catch(() => null)
      const message =
        body && typeof body === 'object'
          ? ((body as { error?: { message?: unknown } }).error?.message ?? null)
          : null
      return typeof message === 'string' && message !== ''
        ? message
        : `The server refused the upload (${res.status}).`
    }
    meta = (await res.json()) as HuntSoundMetaDto
  } catch {
    return 'The upload did not reach the server. Check the connection and try again.'
  }

  // Committed server-side. This console already holds the decoded buffer, so
  // it never re-downloads its own upload; everyone else's console hears about
  // it over the `sounds.changed` frame the server just published.
  fleetSounds.set(event, { name: meta.name, version: meta.version, buffer })
  void idbPut(IDB_FLEET_STORE, event, { name: meta.name, version: meta.version, bytes })
  // Selected out loud for the uploader too: an explicit earlier voice choice
  // would otherwise keep overriding the thing they just uploaded.
  voiceChoice.set(event, FLEET_VOICE_ID)
  writeStored(VOICE_KEY_PREFIX + event, FLEET_VOICE_ID)
  emit()
  return null
}

/** Remove the fleet-wide sound for an event, for every console. */
export async function clearFleetSound(event: RunEvent): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/api/automation/sounds/${event}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (!res.ok) return `The server refused the removal (${res.status}).`
  } catch {
    return 'The removal did not reach the server. Check the connection and try again.'
  }
  if (fleetSounds.delete(event)) {
    void idbDelete(IDB_FLEET_STORE, event)
    if (voiceChoice.get(event) === FLEET_VOICE_ID) {
      voiceChoice.set(event, DEFAULT_VOICE[event])
      writeStored(VOICE_KEY_PREFIX + event, DEFAULT_VOICE[event])
    }
    emit()
  }
  return null
}

/* ------------------------------------------------------------------ *
 * Placement
 *
 * Playback is one sound per event with no coalescing, so an opening that ten
 * customers see at the same instant really does play ten times. Ten identical
 * waveforms starting on the same sample do not sound like ten events — they
 * comb-filter into one loud smeared one, and the information an operator wanted
 * (how many) is destroyed by the very thing meant to convey it.
 *
 * So every customer gets a fixed place in the mix: a position in the stereo
 * field, a few cents of pitch, and a few milliseconds of offset. Ten events
 * then arrive as ten distinguishable things spread across a room. All three are
 * derived from the customer id and nothing else, so the same customer always
 * sounds from the same spot for as long as the console is open — a fleet
 * becomes a place you learn rather than noise you tolerate.
 *
 * The win is shaded far less than the other two: it is musical, it is rare, and
 * a claimed shift should sound the same every time it happens.
 * ------------------------------------------------------------------ */

interface Spread {
  /** Maximum pitch shift, ± cents. 100 is a semitone. */
  cents: number
  /** Maximum stereo displacement, ± where 1 is hard to one side. */
  pan: number
  /** Maximum scheduling offset, ± ms. Below audibility as timing. */
  jitterMs: number
}

const SPREAD: Readonly<Record<RunEvent, Spread>> = Object.freeze({
  /*
   * A sighting is one line per customer per opening — never the bulk arrival
   * the claims and misses are — but an opening several customers are watching
   * still lands as several pings in the same tick, so it takes the same kind
   * of shading at a slightly gentler setting.
   */
  active: { cents: 40, pan: 0.6, jitterMs: 60 },
  /*
   * Noise-based and inharmonic, so these take the widest shading without ever
   * sounding wrong — and they are the events that actually arrive in bulk.
   *
   * The offsets are what turn a bulk arrival into something countable. They
   * were single-digit at first, on the reasoning that the detune alone already
   * made two copies different waveforms — true, and not enough. Ten shots
   * landing inside nine milliseconds is one thick shot, however different the
   * waveforms are, because the ear reads anything under about thirty
   * milliseconds as a single attack. Spread across forty-five it is a volley
   * you can count. Both are far under the ~100ms where an offset starts to
   * read as lateness rather than as texture.
   */
  burst: { cents: 55, pan: 0.6, jitterMs: 45 },
  miss: { cents: 35, pan: 0.55, jitterMs: 55 },
  /*
   * The win is shaded DIFFERENTLY, not less.
   *
   * It was briefly shaded not at all — no detune and no offset — on the
   * reasoning that a win is a chord and a chord arriving a third of a semitone
   * flat sounds like a mistake. That reasoning was right and the conclusion was
   * wrong. Two customers winning together produce two events in the same ingest
   * tick, and `currentTime` does not advance inside a tick, so both voices
   * started on the same sample with the same pitch: identical waveforms summing
   * to twice the amplitude, which the limiter then flattened back down. Three
   * wins sounded like one slightly wider win. The count — the thing worth
   * hearing — was destroyed by the one event that most deserves to be heard.
   *
   * So separation comes from TIME rather than pitch. Up to 150ms of offset puts
   * daylight between two arpeggio onsets while staying far too short to read as
   * lateness, and staggered copies of a rising figure land as a round rather
   * than as a mess. The pan opens up to match. The detune stays token — nine
   * cents is under a tenth of a semitone, less than the spread inside a piano's
   * own unison, so it cannot sound out of tune, and it is there only to stop
   * two sustained tails phase-cancelling against each other.
   */
  won: { cents: 9, pan: 0.5, jitterMs: 150 },
})

/**
 * A stable [0,1) from a string. FNV-1a, seeded so one id yields several
 * independent-looking values rather than three correlated ones.
 */
function hash01(s: string, seed: number): number {
  let h = 2166136261 ^ seed
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return ((h >>> 0) % 1_000_003) / 1_000_003
}

/** A short neutral blip, used only to confirm that switching sound on worked. */
function confirmChirp(c: Ctx, dest: AudioNode, t0: number): void {
  toneLayer(c, dest, t0, { freq: 880, to: 1320, glide: 0.05, peak: 0.2, attack: 0.006, release: 0.14 })
}

/* ------------------------------------------------------------------ *
 * Engine state
 * ------------------------------------------------------------------ */

/**
 * `typeof globalThis` is load-bearing: `AudioContext` is a global declaration
 * rather than a member of the `Window` interface, so intersecting with `Window`
 * alone loses it. `webkitAudioContext` is the Safari spelling.
 */
type Win = Window &
  typeof globalThis & {
    webkitAudioContext?: typeof AudioContext
    acsSfx?: unknown
  }

const storedOn = readStored(ON_KEY)
let enabled = storedOn === null ? DEFAULT_ENABLED : storedOn === '1'
let volume = clampVolume(Number.parseFloat(readStored(VOL_KEY) ?? ''))
let ctx: AudioContext | null = null
let master: GainNode | null = null
/** True once the page has seen a real gesture — the browser's condition for audio. */
let gestured = false
/** Voices currently sounding. Bounds a pathological storm; never trims a normal one. */
let sounding = 0

/**
 * Chosen voice per event, hydrated ONCE at load.
 *
 * Deliberately not read from `localStorage` inside `playRunEvent`: storage is
 * synchronous, and one opening seen by fifty customers announces fifty times in
 * a single tick. A stored id that no longer exists is discarded here rather
 * than at play time, so the hot path never validates anything either.
 */
const voiceChoice = new Map<RunEvent, string>()
for (const event of RUN_EVENTS) {
  const stored = readStored(VOICE_KEY_PREFIX + event)
  // `custom` and `fleet` are accepted provisionally: whether a file actually
  // stands behind either is only known once IndexedDB (and, for fleet, the
  // server) answers, and the hydrate below clears a `custom` choice that
  // turns out to be empty. Until then `voiceFor` falls back to the default
  // synth voice, so no event ever goes silent while loading.
  if (
    stored &&
    (stored === CUSTOM_VOICE_ID ||
      stored === FLEET_VOICE_ID ||
      VOICES[event].some((v) => v.id === stored))
  ) {
    voiceChoice.set(event, stored)
  }
}

/*
 * Hydrate uploaded sounds from IndexedDB, off the critical path.
 *
 * Runs once at load, for both stores: the operator's own uploads and the
 * cached copies of the fleet's. Any file that fails to decode — a format this
 * browser cannot read, or a corrupted row — is dropped from its store rather
 * than retried forever, and the event falls back to its synth voice. The
 * fleet cache is hydrated as-is here and reconciled against the server when
 * the realtime layer calls `refreshFleetSounds`.
 */
if (typeof window !== 'undefined' && typeof indexedDB !== 'undefined') {
  void (async () => {
    try {
      const [customRows, fleetRows] = await Promise.all([
        idbLoadAll(IDB_STORE),
        idbLoadAll(IDB_FLEET_STORE),
      ])
      await Promise.all([
        ...[...customRows].map(async ([event, raw]) => {
          const rec = asCustomRow(raw)
          if (!rec) return
          const sound: CustomSound = { name: rec.name, buffer: null }
          customSounds.set(event, sound)
          try {
            sound.buffer = await decodeBytes(rec.bytes)
          } catch {
            customSounds.delete(event)
            void idbDelete(IDB_STORE, event)
          }
        }),
        ...[...fleetRows].map(async ([event, raw]) => {
          const name = raw['name']
          const version = raw['version']
          const bytes = raw['bytes']
          if (
            typeof name !== 'string' ||
            typeof version !== 'number' ||
            !(bytes instanceof ArrayBuffer)
          ) {
            return
          }
          const sound: FleetSound = { name, version, buffer: null }
          fleetSounds.set(event, sound)
          try {
            sound.buffer = await decodeBytes(bytes)
          } catch {
            fleetSounds.delete(event)
            void idbDelete(IDB_FLEET_STORE, event)
          }
        }),
      ])
    } catch {
      // No IndexedDB (private mode, policy). Uploads simply do not persist.
    }
    for (const event of RUN_EVENTS) {
      // A `custom` choice with no file behind it is cleared for good. A
      // `fleet` choice is left standing even when the cache is empty: the
      // server may still hold the file, and `refreshFleetSounds` settles it —
      // until then the default synth voice covers the gap.
      if (voiceChoice.get(event) === CUSTOM_VOICE_ID && !customSounds.has(event)) {
        voiceChoice.delete(event)
      }
    }
    emit()
  })()
}

/**
 * Ceiling on voices ringing at once — a runaway guard, NOT a mixer.
 *
 * Playback is deliberately one sound per event with no coalescing, so an
 * opening that five hundred customers see at the same instant is meant to play
 * five hundred times. This exists only so a pathological case cannot pin the
 * audio thread, and it must sit far enough above any real simultaneous window
 * that it never actually decides anything.
 *
 * 64 was too close to be that. A fleet of five hundred is the stated target and
 * every one of them can miss on the same opening, so the number that never
 * bites had to be in the hundreds rather than the dozens. It is also less
 * binding than it looks: a slot is held for one voice's own length, and a miss
 * is four hundred milliseconds, so this bounds what overlaps rather than what
 * arrives — a second of solid misses passes about twice this many without ever
 * reaching it.
 */
const MAX_SOUNDING = 256

function clampVolume(v: number): number {
  if (!Number.isFinite(v)) return DEFAULT_VOLUME
  return Math.min(1, Math.max(0, v))
}

/** Perceptual, not linear — a linear slider spends most of its travel inaudibly loud. */
function gainFor(v: number): number {
  return Math.pow(v, 1.6) * 0.9
}

/* ------------------------------------------------------------------ *
 * Subscribers (for the one button that renders this state)
 * ------------------------------------------------------------------ */

const listeners = new Set<() => void>()

export function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

function emit(): void {
  for (const fn of [...listeners]) {
    try {
      fn()
    } catch {
      /* a bad subscriber must not stop the others */
    }
  }
}

/* ------------------------------------------------------------------ *
 * Gesture unlock
 * ------------------------------------------------------------------ */

if (typeof window !== 'undefined') {
  const mark = (): void => {
    gestured = true
    window.removeEventListener('pointerdown', mark, true)
    window.removeEventListener('keydown', mark, true)
    if (!enabled) return
    /*
     * Open the device on the gesture itself rather than on the first event.
     *
     * Two reasons. The reverb impulse and the noise buffer are ~250k random
     * samples between them, and paying for those during an opening is exactly
     * the wrong moment; here it lands on a click nobody is timing. And the
     * button is showing "waiting for a click" until this happens, so it has to
     * be told the moment it stops being true.
     */
    ensureContext()
    emit()
  }
  // Capture phase, so a click that something else calls `stopPropagation` on
  // still counts — this only records that a gesture happened.
  window.addEventListener('pointerdown', mark, true)
  window.addEventListener('keydown', mark, true)
}

/* ------------------------------------------------------------------ *
 * The audio graph
 * ------------------------------------------------------------------ */

/**
 * Build the context, or return the one already built.
 *
 * Returns null when sound is off or no gesture has happened yet, and callers
 * treat that as "drop this sound" rather than as an error. Constructing a
 * context before a gesture would leave it suspended and, in Chrome, log a
 * warning on every load of a console that may never want audio at all.
 */
function ensureContext(): AudioContext | null {
  if (!enabled || !gestured) return null
  if (ctx) return ctx
  try {
    const w = window as Win
    const Ctor = w.AudioContext ?? w.webkitAudioContext
    if (!Ctor) return null

    const c = new Ctor()
    const g = c.createGain()
    g.gain.value = gainFor(volume)

    /*
     * A limiter, and with one-sound-per-event it is load-bearing rather than
     * polish: transients this sharp sum past full scale the moment two land
     * together, and clipping is what makes a busy window sound broken instead
     * of busy.
     */
    const comp = c.createDynamicsCompressor()
    comp.threshold.value = -12
    comp.knee.value = 10
    comp.ratio.value = 8
    comp.attack.value = 0.002
    comp.release.value = 0.12

    g.connect(comp)
    comp.connect(c.destination)

    /*
     * The shared room, built now rather than on the first win: ~150k random
     * samples is not much, but it is enough to hitch the one sound that must
     * never hitch, and this runs on a click rather than during a window.
     */
    const send = c.createGain()
    send.gain.value = 1
    const conv = c.createConvolver()
    conv.buffer = reverbIR(c, 1.6, 2.8)
    const ret = c.createGain()
    ret.gain.value = 0.85
    send.connect(conv)
    conv.connect(ret)
    ret.connect(g)

    // Warm the noise buffer too, for the same reason.
    noise(c)

    ctx = c
    master = g
    wetSend = send
    return c
  } catch {
    // No Web Audio, or the context limit is reached. Sound is simply unavailable.
    return null
  }
}

/**
 * Play one voice now, at a given place in the mix.
 *
 * Never throws, never blocks, never renders. `place` is null for previews and
 * for the confirmation chirp, which belong dead centre and unshaded.
 *
 * `track` registers the voice as cuttable under its customer — see the
 * handoff-cut block above. Null for previews, the chirp, and every event
 * nothing silences.
 */
function fire(
  voice: Voice,
  place: { cents: number; pan: number; jitterMs: number } | null,
  track: { customerId: string; event: RunEvent } | null = null
): void {
  const c = ensureContext()
  if (!c || !master) return

  if (c.state === 'suspended') {
    /*
     * Scheduling into a suspended context would stack every dropped sound onto
     * the instant it resumes, because `currentTime` does not advance while it
     * is asleep. Kick it and drop this one — the next event will be heard.
     */
    void c.resume().catch(() => {})
    return
  }
  if (sounding >= MAX_SOUNDING) return

  /*
   * The cut bus: one gain the whole voice passes through, held under the
   * customer's id so THEIR next handover can fade THIS voice without touching
   * any other customer's. Created only for tracked events — the sighting and
   * the claim — so the fire-and-forget sounds cost exactly what they always
   * did.
   */
  let root: AudioNode = master
  let cut: GainNode | null = null
  let cutSet: Map<GainNode, RunEvent> | null = null
  if (track) {
    try {
      cut = c.createGain()
      cut.connect(master)
      root = cut
      cutSet = ringing.get(track.customerId) ?? null
      if (!cutSet) {
        cutSet = new Map()
        ringing.set(track.customerId, cutSet)
      }
      cutSet.set(cut, track.event)
    } catch {
      cut = null
      cutSet = null
      root = master
    }
  }

  // One panner per voice, so each event occupies its own place. `dest` for the
  // whole voice, while the reverb send stays shared and wide — see `wetSend`.
  let dest: AudioNode = root
  let panner: StereoPannerNode | null = null
  if (place && place.pan !== 0 && typeof c.createStereoPanner === 'function') {
    try {
      panner = c.createStereoPanner()
      panner.pan.value = Math.max(-1, Math.min(1, place.pan))
      panner.connect(root)
      dest = panner
    } catch {
      // Not available (very old Safari). Centre is a fine degradation.
      panner = null
      dest = root
    }
  }

  shadeCents = place ? place.cents : 0
  // A cent is 2^(1/1200); the same ratio moves noise's spectral centre by ear.
  shadeRate = place ? Math.pow(2, place.cents / 1200) : 1

  try {
    voice.build(c, dest, c.currentTime + (place ? place.jitterMs / 1000 : 0))
  } catch {
    // A malformed voice must not take the console with it — and must not
    // leave its cut bus behind either: registered above, it would otherwise
    // sit in `ringing` forever claiming a sound that never played.
    if (cut) {
      try {
        cut.disconnect()
      } catch {
        /* already gone */
      }
      if (cutSet) {
        cutSet.delete(cut)
        if (cutSet.size === 0 && track && ringing.get(track.customerId) === cutSet) {
          ringing.delete(track.customerId)
        }
      }
    }
    return
  } finally {
    // Always restored, including on the throw above, or the next voice would
    // inherit this one's shading.
    shadeCents = 0
    shadeRate = 1
  }

  sounding += 1
  window.setTimeout(
    () => {
      sounding = Math.max(0, sounding - 1)
      // Release the panner's edge into the master so the node graph does not
      // retain one dangling connection per event over a long session.
      if (panner) {
        try {
          panner.disconnect()
        } catch {
          /* already gone */
        }
      }
      if (cut) {
        try {
          cut.disconnect()
        } catch {
          /* already gone */
        }
        // Against the map captured at registration, not looked up now: an
        // outcome may have already dropped this customer's map and a NEWER
        // voice may own a fresh one under the same id — which this timer must
        // not touch.
        if (cutSet) {
          cutSet.delete(cut)
          if (cutSet.size === 0 && track && ringing.get(track.customerId) === cutSet) {
            ringing.delete(track.customerId)
          }
        }
      }
    },
    Math.ceil(voice.dur * 1000) + 220
  )
}

/* ------------------------------------------------------------------ *
 * The handoff cut
 *
 * A customer's claim silences THEIR still-ringing sighting, and nobody
 * else's: the burst firing while that same customer's active-hit sound is
 * mid-ring fades the sighting out in ~12ms — under the click threshold, over
 * the "did two sounds overlap" threshold — so the claim is heard clean. Every
 * other customer's sounds are untouched; the map below is keyed by customer
 * id and a cut only ever reads its own key.
 *
 * The rules, as a small matrix rather than prose in the hot path — the story
 * hands over twice, and each handover silences what it supersedes: the claim
 * firing cuts that customer's still-ringing sighting, and the OUTCOME cuts
 * their still-ringing claim (plus a sighting that never handed over, which
 * the claim would have cut anyway).
 *
 * Both outcomes, not just the win. A miss used to be pure fire-and-forget,
 * so a burst refused within a second or two played its miss OVER a claim
 * sound that was still announcing a chase already lost — two sounds saying
 * opposite things at once, for the same customer. Once the outcome is in,
 * that customer's heads-up sounds have nothing left to say.
 *
 * Neither outcome is ever cut: a win always rings out in full, and so does a
 * miss. Nothing tracks them, so nothing can reach them.
 *
 *   tracked (cuttable): active, burst
 *   burst cuts:         active
 *   won cuts:           active, burst
 *   miss cuts:          active, burst
 *   nothing cuts a won or a miss.
 *
 * Cost: one extra GainNode per tracked voice, one Map lookup per outcome —
 * both O(1), no allocation on the outcome path beyond the fade itself, and a
 * console with sound off still pays nothing because `playRunEvent` returns
 * before any of this. Entries remove themselves on the voice's existing
 * cleanup timer, so the map is bounded by what is audibly ringing (itself
 * bounded by `MAX_SOUNDING`) however many customers run in parallel.
 * ------------------------------------------------------------------ */

const TRACKED: ReadonlySet<RunEvent> = new Set<RunEvent>(['active', 'burst'])

/** What each event silences. Absent key = cuts nothing. */
const CUTS: Partial<Record<RunEvent, ReadonlySet<RunEvent>>> = {
  burst: new Set<RunEvent>(['active']),
  won: new Set<RunEvent>(['active', 'burst']),
  miss: new Set<RunEvent>(['active', 'burst']),
}

/** Per customer: each ringing cuttable voice's cut bus, tagged by its event. */
const ringing = new Map<string, Map<GainNode, RunEvent>>()

/** Fade out this customer's ringing voices that `by` outranks. Never throws. */
function cutRinging(customerId: string, by: RunEvent): void {
  const cuts = CUTS[by]
  if (!cuts) return
  const set = ringing.get(customerId)
  if (!set || !ctx) return
  const t = ctx.currentTime
  for (const [g, event] of set) {
    if (!cuts.has(event)) continue
    try {
      g.gain.setTargetAtTime(0.0001, t, 0.012)
    } catch {
      /* a dead node fades nothing, which is already the goal */
    }
    // Removed now rather than left for the voice timer, so a second outcome
    // in the same tick does not fade the same node twice.
    set.delete(g)
  }
  if (set.size === 0) ringing.delete(customerId)
}

/** Where a given customer's events sit in the mix. See `SPREAD`. */
function placeFor(event: RunEvent, customerId: string | null) {
  const s = SPREAD[event]
  if (!s || !customerId) return null
  const a = hash01(customerId, 0x9e3779b1)
  const b = hash01(customerId, 0x85ebca6b)
  const c = hash01(customerId, 0xc2b2ae35)
  return {
    cents: (a * 2 - 1) * s.cents,
    pan: (b * 2 - 1) * s.pan,
    jitterMs: c * s.jitterMs,
  }
}

function voiceFor(event: RunEvent): Voice | null {
  /*
   * Defensive on `event` itself, not only on the id.
   *
   * Run-log lines are read back from Redis as unvalidated JSON and arrive over
   * the wire from an API that may be a version ahead, so `event` can hold a
   * value this build has never heard of however the type reads here. An
   * unknown event is silent; an unknown id falls back to the first voice
   * rather than silencing an event we do understand.
   */
  const list = VOICES[event] as readonly Voice[] | undefined
  if (!list || list.length === 0) return null
  /*
   * Precedence: the operator's explicit choice, else the fleet's upload when
   * one exists, else the built-in default. An explicit choice — a synth voice
   * or this browser's own file — always beats the fleet upload, so uploading
   * for everyone never overrides someone who deliberately picked otherwise;
   * an operator who never touched the picker hears what the fleet hears.
   */
  const wanted =
    voiceChoice.get(event) ??
    (fleetSounds.has(event) ? FLEET_VOICE_ID : DEFAULT_VOICE[event])
  if (wanted === CUSTOM_VOICE_ID || wanted === FLEET_VOICE_ID) {
    // The uploaded file, when it is decoded and ready. When it is not —
    // still hydrating, still downloading, or the row vanished — the event
    // falls back to its default synth voice rather than going silent,
    // because a missed announcement costs more than a wrong-flavoured one.
    const uploaded = wanted === CUSTOM_VOICE_ID ? customVoice(event) : fleetVoice(event)
    if (uploaded) return uploaded
    return list.find((v) => v.id === DEFAULT_VOICE[event]) ?? list[0] ?? null
  }
  return list.find((v) => v.id === wanted) ?? list[0] ?? null
}

/* ------------------------------------------------------------------ *
 * Public surface
 * ------------------------------------------------------------------ */

/**
 * Whether the caller should bother looking at lines at all.
 *
 * One boolean read. `useRealtime` checks this before touching a batch, so a
 * console with sound off does no per-line work whatsoever.
 */
export function wantsEvents(): boolean {
  return enabled
}

/**
 * Announce one run event. Safe to call at any rate, from anywhere.
 *
 * `customerId` places the sound — see `SPREAD`. It is optional so a caller
 * without one still gets a centred sound rather than silence.
 */
export function playRunEvent(event: RunEvent, customerId?: string | null): void {
  if (!enabled) return
  const voice = voiceFor(event)
  if (!voice) return
  const id = customerId ?? null
  // This customer's claim silences this customer's ringing sighting — and
  // only theirs. See the handoff-cut block for the matrix and the costs.
  if (id) cutRinging(id, event)
  fire(voice, placeFor(event, id), id && TRACKED.has(event) ? { customerId: id, event } : null)
}

export function getEnabled(): boolean {
  return enabled
}

/**
 * Whether a sound would actually be heard right now.
 *
 * Distinct from `getEnabled` on purpose: sound can be switched on and still be
 * inaudible, because the browser has not yet seen the gesture it requires
 * before any page may open an audio device. A control that showed only
 * `enabled` would claim the console was speaking while it was not, and the
 * operator's conclusion would be that the feature is broken.
 */
export function getReady(): boolean {
  return enabled && gestured
}

export function setEnabled(next: boolean): void {
  if (enabled === next) return
  enabled = next
  writeStored(ON_KEY, next ? '1' : '0')

  if (next) {
    // The click that asked for sound is itself the gesture that unlocks it, so
    // the confirmation is audible on the very first press.
    gestured = true
    const c = ensureContext()
    if (c && master) {
      if (c.state === 'suspended') void c.resume().catch(() => {})
      try {
        // Centred and unshaded — `shadeCents` is neutral outside `fire`.
        confirmChirp(c, master, c.currentTime + 0.02)
      } catch {
        /* ignore */
      }
    }
  } else if (ctx && ctx.state === 'running') {
    // Suspend rather than close: closing is irreversible, and the operator may
    // well switch sound back on a minute later.
    void ctx.suspend().catch(() => {})
  }

  emit()
}

export function getVolume(): number {
  return volume
}

export function setVolume(next: number): void {
  const v = clampVolume(next)
  if (v === volume) return
  volume = v
  writeStored(VOL_KEY, String(v))
  if (master && ctx) {
    // Ramped, not assigned: a step change in gain is an audible click.
    master.gain.setTargetAtTime(gainFor(v), ctx.currentTime, 0.02)
  }
  emit()
}

export function getVoice(event: RunEvent): string {
  return voiceFor(event)?.id ?? DEFAULT_VOICE[event]
}

/** Change what an event sounds like. Persists, and applies to the next event. */
export function setVoice(event: RunEvent, id: string): void {
  const valid =
    id === CUSTOM_VOICE_ID
      ? customSounds.has(event)
      : id === FLEET_VOICE_ID
        ? fleetSounds.has(event)
        : VOICES[event].some((v) => v.id === id)
  if (!valid) return
  voiceChoice.set(event, id)
  writeStored(VOICE_KEY_PREFIX + event, id)
  emit()
}

/**
 * Play a voice without waiting for the event. Used to audition.
 *
 * `customerId` is accepted so an audition can be heard from the same place a
 * given customer will sound from; omitted, it plays centred and unshaded,
 * which is the honest way to compare two voices against each other.
 */
export function preview(event: RunEvent, id?: string, customerId?: string | null): void {
  const list = VOICES[event] as readonly Voice[] | undefined
  if (!list) return
  const voice =
    id === CUSTOM_VOICE_ID || id === FLEET_VOICE_ID
      ? // Not yet decoded (hydration or download still in flight)? Fall back
        // exactly the way playback does, so an audition never claims silence
        // where a real event would sound.
        ((id === CUSTOM_VOICE_ID ? customVoice(event) : fleetVoice(event)) ?? voiceFor(event))
      : ((id ? list.find((v) => v.id === id) : null) ?? voiceFor(event))
  if (!voice) return
  gestured = true
  const c = ensureContext()
  if (c && c.state === 'suspended') void c.resume().catch(() => {})
  fire(voice, placeFor(event, customerId ?? null))
}

/** The uploaded file's name for an event, or null when none is stored. */
export function getCustomSoundName(event: RunEvent): string | null {
  return customSounds.get(event)?.name ?? null
}

/** Whether the uploaded file is decoded and would actually play right now. */
export function isCustomSoundReady(event: RunEvent): boolean {
  return customSounds.get(event)?.buffer != null
}

/**
 * Store an uploaded file as an event's sound and select it.
 *
 * Everything expensive happens HERE, once — reading the file, decoding it,
 * writing IndexedDB — so that playing it later costs nothing. Returns an error
 * sentence to show beside the picker, or null on success. Rejections happen
 * before anything is stored, so a failed upload never disturbs the sound that
 * was already in place.
 */
export async function setCustomSound(event: RunEvent, file: File): Promise<string | null> {
  if (file.size > MAX_CUSTOM_BYTES) {
    return `That file is ${(file.size / (1024 * 1024)).toFixed(1)}MB — the limit is ${Math.round(MAX_CUSTOM_BYTES / (1024 * 1024))}MB.`
  }
  let bytes: ArrayBuffer
  try {
    bytes = await file.arrayBuffer()
  } catch {
    return 'Could not read the file.'
  }
  let buffer: AudioBuffer
  try {
    buffer = await decodeBytes(bytes)
  } catch {
    return 'This browser could not decode that file. MP3, WAV and OGG are safe choices.'
  }
  if (buffer.duration > MAX_CUSTOM_SECONDS) {
    return `That clip runs ${buffer.duration.toFixed(1)}s — the limit is ${MAX_CUSTOM_SECONDS}s, so a storm of events cannot stack minutes of audio.`
  }

  customSounds.set(event, { name: file.name, buffer })
  voiceChoice.set(event, CUSTOM_VOICE_ID)
  writeStored(VOICE_KEY_PREFIX + event, CUSTOM_VOICE_ID)
  emit()
  try {
    await idbPut(IDB_STORE, event, { name: file.name, bytes })
  } catch {
    // Storage refused (quota, private mode). The sound still plays for this
    // session; it simply will not survive a reload.
    return 'Saved for this session, but the browser refused to store it — it will not survive a reload.'
  }
  return null
}

/** Remove an event's uploaded sound and fall back to its synthesised voice. */
export function clearCustomSound(event: RunEvent): void {
  if (!customSounds.delete(event)) return
  void idbDelete(IDB_STORE, event)
  if (voiceChoice.get(event) === CUSTOM_VOICE_ID) {
    voiceChoice.set(event, DEFAULT_VOICE[event])
    writeStored(VOICE_KEY_PREFIX + event, DEFAULT_VOICE[event])
  }
  emit()
}

/**
 * A console handle, so sounds can be auditioned and swapped from devtools
 * without a redeploy:
 *
 *   acsSfx.preview('won', 'bloom')
 *   acsSfx.setVoice('won', 'bloom')
 *
 * Attached rather than hidden behind a build flag because this is an internal
 * operations console and the alternative is editing a constant and shipping.
 */
if (typeof window !== 'undefined') {
  ;(window as Win).acsSfx = {
    voices: VOICES,
    preview,
    setVoice,
    getVoice,
    setEnabled,
    getEnabled,
    setVolume,
    getVolume,
    setCustomSound,
    clearCustomSound,
    getCustomSoundName,
    setFleetSound,
    clearFleetSound,
    getFleetSoundName,
    refreshFleetSounds,
  }
}
